import React, { ComponentProps, useMemo, useState } from 'react';
import { Autocomplete } from '@workboard/wobo-ui';

import { Option, useAutocomplete } from '../utils/useAutocomplete';
import { UserBadge } from './badge';

type AutocompleteProps = ComponentProps<typeof Autocomplete>;

export interface IncomingUser {
  id: string;
  label: string;
  pic: string;
}

export function useUserAutocomplete(
  value: string,
  initialData?: Partial<IncomingUser>,
  filterOption?: (item: IncomingUser) => boolean
) {
  return useAutocomplete<IncomingUser, IncomingUser[], IncomingUser>({
    url: `activity/getOrgUsers`,
    value,
    initialData,
    filterOption,
    getParams: (q: string) => ({
      term: q,
    }),
  });
}

const Template = (opt: IncomingUser) => {
  const { pic, label } = opt;
  return (
    <UserBadge
      imgUrl={pic}
      showText
      size={16}
      title={false}
      userName={label}
      userRole=""
    />
  );
};
interface Props extends AutocompleteProps {
  value: string;
  initialData?: Partial<IncomingUser>;
  filter?: (item: IncomingUser) => boolean;
}

export const UserAutocompleteField = React.forwardRef(
  ({ value, initialData, filter, ...props }: Props, ref) => {
    const [users, searchUsers, loading] = useUserAutocomplete(
      value,
      initialData,
      filter
    );
    const options = useMemo(
      () => (filter ? users.filter(filter) : users),
      [users, filter]
    );
    const [inputValue, setInputValue] = useState('');
    const valObj = users.find((u) => u?.id === value);
    return (
      <Autocomplete
        {...props}
        ref={ref}
        startMatch={false}
        loading={loading}
        onInputChange={(e, text) => {
          if (e) {
            setInputValue(text);
            searchUsers(text);
          }
        }}
        options={options}
        inputValue={inputValue}
        getOptionSelected={(user: Option, val) => user?.id === val?.id}
        value={valObj || null}
        template={Template}
      />
    );
  }
);
